import { BaseView } from "./base-view";
import { Navigation } from "../navigation";
import { AssignmentDemand } from "../models/AssignmentDemand";
import { DemandDetailView } from "./demand-detail-view";
import type mdbTypes from '../ext/mdb.min.js';
declare const mdb: typeof mdbTypes
import { CellComponent, ColumnDefinition, RowComponent, TabulatorFull } from "tabulator-tables";
import _ from "lodash";
import { PageContext } from "../page-context";
import { Project } from "../models/Project";
import { checkHeaderFilterFunctionInput, containsString, createNameIconElementWith2Initials, formatLocaleDateString, setToEndOfDay } from "../common";
import { Skill } from "../models/Skill";
import { SkillSet } from "../models/SkillSet";

export class DemandView extends BaseView {
    static title = "Demands";
    static icon = "bi bi-card-list";
    filteredView!: string;
    filteredViews: { [key: string]: () => { [key: string]: any } } = {
        "all": async () => true,
        "open": () => {
            return {
                "status": ["Open"]
            }
        },
        "open market": () => {
            return {
                "status": ["Open"],
                "customer": this.assignments.map(a => a.customer)
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .filter(c => c && !c.includes("Kärcher") && c != "Internal")
            }
        },
        "sourcing": () => {
            return {
                "status": ["Open (sourcing)"]
            }
        },
        "clarification needed": () => {
            return {
                "status": ["Clarification needed"]
            }
        }
    }
    assignments!: AssignmentDemand[];
    projectRoles!: string[];
    seniorities!: string[];
    projects!: Project[]
    skills!: { [key: string]: string[] };
    skillCategoryAndName: Skill[] = [];
    keywords: string[] = [];

    /**
     * constructor
     * @param {PageContext} context - The context of the page.
     * @param {string} [container=".main"] - The container to render the view in.
     */
    constructor(context: PageContext, container = ".main") {
        super(context, container);

        this.filteredView = context.params.filteredView || "open";

        let versionCheck = this.checkVersionAsync();

        Promise.all([versionCheck]).then(() => {
            let dataLoad = [
                this.context.store.getAssignmentsWithCalculatedEmployeesWeeklyHours(),
                this.context.store.getProjectRoles(),
                this.context.store.getSeniorities(),
                this.context.store.getProjects(),
                this.context.store.getSkillStructure(),
            ]

            Promise.all(dataLoad).then((values) => {
                this.assignments = values[0] as AssignmentDemand[];
                this.projectRoles = values[1] as string[];
                this.seniorities = values[2] as string[];
                this.projects = values[3] as Project[];
                this.skills = values[4] as { [key: string]: string[] };

                Object.keys(this.skills).forEach((category) => {
                    this.skills[category].forEach((skill) => {
                        this.skillCategoryAndName.push(new Skill(category.replace(" - ", " ").replace(" ", ""), skill, 0));
                    });
                });

                this.projects.map((project) => {
                    project.keywords ? project.keywords.forEach((keyword) => {
                        if (this.keywords.indexOf(keyword) === -1) {
                            this.keywords.push(keyword);
                        }
                    }) : null;
                });

                this.onDataLoaded();
                if (this.context.params.assignmentId) {
                    this.showDemandDetailById(this.context.params.assignmentId);
                } else if (this.context.params.data) {
                    this.showDemandDetailByData(this.context.params.data);
                }
            });
        });

    }



    connectedCallback(): this {
        super.connectedCallback();
        this.innerHTML = `<span class="placeholder col-7"></span>`;
        return this;
    }

    async showDemandDetailById(assignmentId: string) {
        this.table?.on("tableBuilt", async () => {
            for (const row of this.table.getRows()) {
                var data = row.getData() as AssignmentDemand;
                if (data.id.toString() === assignmentId.toString()) {
                    //TODO check if this was working?
                    //this.table.scrollToRow(row);
                    this.table.selectRow(row.getData().id);
                    var result = await this.showDemandDetailByData(data);
                    Navigation.restorePreviousHashUrl();

                    if (result === null) {
                        //delete row
                        row.delete();
                    } else if (result) {
                        var resultValue = result;
                        this.context.store.calculateTotalWeekly(resultValue);
                        row.update(data);
                    }
                }
            }
        });
    }

    async showDemandDetailByData(data: AssignmentDemand): Promise<AssignmentDemand> {
        var ctx = Object.assign(this.context, { data: new AssignmentDemand(data) });
        return await DemandDetailView.showAsModal(ctx);
    }

    onDataLoaded() {
        this.innerHTML = `
        <div>
            <h1><i class="${DemandView.icon}"></i> Demands</h1>
            <p>
                Demands are the requests for resources to work on projects. They are created by project managers and are assigned to employees.
                <a class="help-page-link-icon" href="#/help#demands" title="Help page">
                    <i class="bi bi-info-circle"></i>
                </a>
            </p>

            <div class="row mb-1">
                <div class="col-auto me-auto">
                    <button class="btn btn-sm btn-primary btn-create-demand" title="Create Demand">Create Demand</button>

                    <div class="btn-group btn-group-sm btn-filteredViews" role="group"></div>

                    <button accesskey="r" class="btn btn-sm btn-outline-secondary btn-refresh" title="Reload history"><i class="bi bi-arrow-clockwise"></i></button>
                </div>

                <div class="col-auto">
                    <button accesskey="e" class="btn btn-sm btn-outline-secondary btn-export-csv" title="Export CSV"><i class="bi bi-cloud-arrow-down"></i></button>
                    <button accesskey="c" class="btn btn-sm btn-outline-secondary btn-toggle-cols" title="Show columns"><i class="bi bi-layout-three-columns"></i></button>
                </div>
            </div>
        </div>
        <div id="assignment-table" class="remaining-height"></div>`;

        let elem = $(this);

        let createButton = elem.find(".btn-create-demand");
        if (this.context.user.hasEditPermission()) {
            createButton.click(async (e) => {
                let ctx = Object.assign(this.context, { data: AssignmentDemand.createNew({}) });
                await DemandDetailView.showAsModal(ctx);
            });
        }
        else {
            createButton.hide();
        }

        elem.find(".btn-export-csv").click((e) => {
            this.table.download("csv", "export.csv");
        });

        const btnfilteredViews = elem.find(".btn-filteredViews");
        Object.keys(this.filteredViews).forEach((filterName) => {
            const filterButton = $(`<button class="btn btn-sm btn-${this.filteredView === filterName ? "" : "outline-"}secondary" data-view="${filterName}">${filterName}</button>`);
            filterButton.click((e) => {
                $(e.target).parent().children().removeClass("btn-secondary").addClass("btn-outline-secondary");
                $(e.target).removeClass("btn-outline-secondary").addClass("btn-secondary");
                this.filteredView = $(e.target).data("view");
                this.applyFilteredView();
                this.calculateRowsForEachFilter();
            });
            btnfilteredViews.append(filterButton);
        });

        elem.find(".btn-refresh").click((e) => {
            this.showLoading();
            this.context.store.assignmentChangeUnsubscribe(this.subscribedAssignmentChangeHandler);
            this.context.store.clearSubscribedAssignments().then((x) =>{
                this.context.store.getAssignmentsWithCalculatedEmployeesWeeklyHours().then((data) => {
                    this.assignments = data;
                    this.table.replaceData(data);
                    this.calculateRowsForEachFilter();
                    this.context.store.assignmentChangeSubscribe(this.subscribedAssignmentChangeHandler);
                    this.hideLoading();
                });
            });
        });

        this.context.store.assignmentChangeUnsubscribeAll();
        this.context.store.assignmentChangeSubscribe(this.subscribedAssignmentChangeHandler);
        this.addTable();
        this.columnToggler($(".btn-toggle-cols")[0], this.table);
    }

    async onRowClick(_e: UIEvent, row: RowComponent) {
        let data = row.getData();
        row.getTable().deselectRow();
        row.getTable().selectRow(row.getData().id);

        Navigation.navigateHash("/demands/detail", { assignmentId: data.id }, true);
        var ctx = Object.assign(this.context, { data: data });
        await DemandDetailView.showAsModal(ctx);
        Navigation.restorePreviousHashUrl();
    }

    applyFilteredView() {
        this.table.clearFilter(true);
        this.table.clearHeaderFilter();
        this.table.setSort([{ column: "dueDate", dir: "asc" }]);
        const filter = this.filteredViews[this.filteredView]();
        Object.keys(filter).forEach((column) => {
            this.table.setHeaderFilterValue(column, filter[column]);
        });
        this.context.params.filteredView = this.filteredView;
        Navigation.navigateHash(null, this.context.params, true);
    }

    async calculateRowsForEachFilter() {
        Object.keys(this.filteredViews).forEach((filterName) => {
            let rows = this.assignments;
            const filter = this.filteredViews[filterName]();
            Object.keys(filter).forEach((column) => {
                rows = rows.filter((row) => {
                    if (Array.isArray(filter[column])) {
                        return filter[column].some((val) => val === row[column as keyof AssignmentDemand]);
                    }
                    return filter[column] === row[column as keyof AssignmentDemand];
                });
            });
            $(this).find(`.btn-filteredViews button[data-view='${filterName}']`)
                .text(`${filterName} (${rows.length})`);
        });
    }

    stripHtmlTags(html: string): string {
        if (!html) return '';
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || '';
    }

    formatDueDate(cell: CellComponent) {
        const val = cell.getValue();
        if (!val)
            return "";

        if (val < new Date()) {
            const row = cell.getRow();
            const data = row.getData();

            if (data.status.toLowerCase() === "open") {
                cell.getElement().style.color = "#FF0000";
            }
        }

        return formatLocaleDateString(val);
    }

    addTable() {

        const columns: ColumnDefinition[] = [
            {
                title: "Project",
                field: "projectId",
                frozen: true,
                width: 200,
                headerFilter: "list",
                headerFilterFunc: (headerValue, rowValue) => {
                    return checkHeaderFilterFunctionInput(headerValue, rowValue) && containsString(rowValue, headerValue);
                },
                headerFilterParams: {
                    sort: "asc",
                    values: this.assignments
                        .map(a => a.projectId)
                        .filter((v, i, a) => v !== null && v !== undefined && a.indexOf(v) === i),
                    autocomplete: true,
                },
                tooltip: true
            },
            {
                title: "Project Name",
                field: "projectName",
                visible: false,
                width: 200,
                headerFilter: "list",
                headerFilterFunc: (headerValue, rowValue, rowData) => {
                    const project = this.projects.find(p => p.id === rowData.projectId);
                    var projectName = project ? project.projectName : "Unassigned";
                    return checkHeaderFilterFunctionInput(headerValue, rowValue) && containsString(projectName, headerValue);
                },
                headerFilterParams: {
                    sort: "asc",
                    values: this.assignments
                        .map(a => {
                            const project = this.projects.find(p => p.id === a.projectId);
                            return project ? project.projectName : "Unassigned";
                        })
                        .filter((v, i, a) => v !== null && v !== undefined && a.indexOf(v) === i),
                    autocomplete: true
                },
                tooltip: true,
                mutator: (value, data, type, params, component) => {
                    return data.projectId;
                },
                formatter: (cell) => {
                    const project = this.projects.find(p => p.id === cell.getValue());
                    return project ? project.projectName : "Unassigned";
                }
            },
            {
                title: "Originating project",
                field: "originatingProjectId",
                visible: false,
                frozen: true,
                width: 200,
                headerFilter: "input",
                tooltip: true
            },
            {
                title: "Customer",
                field: "customer",
                headerFilter: "list",
                headerFilterFunc: (headerValue, rowValue) => {
                    if (!Array.isArray(headerValue)) headerValue = [headerValue];
                    if (headerValue.length === 0) return true;
                    if (rowValue === null || rowValue === undefined) return false;
                    return headerValue.some((val: string) => val === rowValue);

                },
                headerFilterParams: {
                    sort: "asc",
                    values: this.assignments
                        .map(a => a.customer)
                        .filter((v, i, a) => v !== null && v !== undefined && a.indexOf(v) === i),
                    multiselect: true
                },
                tooltip: true
            },
            {
                title: "Classification",
                field: "projectName",
                width: 200,
                headerFilter: "list",
                headerFilterParams: {
                    values: this.keywords
                },
                formatter: (cell) => {
                    const keywords = this.projects.find((project) => project.id === cell.getValue())?.keywords || [];
                    if (keywords.length === 0) return "";
                    return keywords.map((keyword: string) => `<span class="badge bg-secondary">${keyword}</span>`).join(" ");
                },
                headerFilterFunc: (headerValue, rowValue, rowData) => {
                    const keywords = this.projects.find((project) => project.id === rowValue)?.keywords || [];
                    return keywords.some((keyword) => keyword.toLowerCase().includes(headerValue.toLowerCase()));
                },
                tooltip: true,
                visible: false
            },
            {
                title: "Description",
                field: "description",
                headerFilter: "input",
                tooltip: true,
                formatter: (cell) => {
                    const value = cell.getValue();
                    return this.stripHtmlTags(value);
                }
            },
            {
                title: "Responsible",
                field: "responsible",
                headerFilter: "list",
                headerFilterFunc: (headerValue, rowValue) => {
                    return checkHeaderFilterFunctionInput(headerValue, rowValue) && containsString(rowValue, headerValue);
                },
                headerFilterParams: {
                    sort: "asc",
                    values: this.assignments
                        .map(a => a.responsible)
                        .filter((v, i, a) => v !== null && v !== undefined && a.indexOf(v) === i),
                    autocomplete: true
                },
                tooltip: true
            },
            {
                title: "Role",
                field: "role",
                headerFilter: "list",
                headerFilterParams: {
                    sort: "asc",
                    values: this.projectRoles,
                },
                tooltip: true
            },
            {
                title: "Seniority",
                field: "seniority",
                width: 100,
                headerFilter: "list",
                headerFilterParams: {
                    sort: "asc",
                    values: this.seniorities,
                },
                tooltip: true
            },
            {
                title: "Priority",
                field: "priority",
                visible: false,
                headerFilter: "input",
                tooltip: true
            },
            {
                field: "startDate",
                title: "Start date",
                formatter: (cell) => formatLocaleDateString(cell.getValue()),
                sorter: (a, b) => { return new Date(a).getTime() - new Date(b).getTime() },
                headerFilterFunc: (headerValue, rowValue, rowData) => {
                    return checkHeaderFilterFunctionInput(headerValue, rowValue) && rowData.startDate > setToEndOfDay(new Date(headerValue));
                },
                visible: false,
                headerFilter: "date",
                tooltip: true
            },
            {
                field: "endDate",
                title: "End date",
                formatter: (cell) => formatLocaleDateString(cell.getValue()),
                sorter: (a, b) => { return new Date(a).getTime() - new Date(b).getTime() },
                headerFilterFunc: (headerValue, rowValue, rowData) => {
                    return checkHeaderFilterFunctionInput(headerValue, rowValue) && rowData.endDate < setToEndOfDay(new Date(headerValue));
                },
                visible: false,
                headerFilter: "date",
                tooltip: true
            },
            {
                field: "pdTotalPlanned",
                title: "PDs",
                visible: false,
                headerFilter: "input",
            },
            {
                title: "Due date",
                field: "dueDate",
                width: 100,
                formatter: this.formatDueDate,
                sorter: (a, b) => { return new Date(a).getTime() - new Date(b).getTime() },
                headerFilterFunc: (headerValue, rowValue, rowData) => {
                    return checkHeaderFilterFunctionInput(headerValue, rowValue) && rowData.dueDate < setToEndOfDay(new Date(headerValue));
                },
                headerFilter: "date",
                tooltip: true
            },
            {
                title: "Skills",
                field: "skills",
                headerSort: false,
                headerFilter: this.createMultiselectHeaderFilter(this.skillCategoryAndName),
                headerFilterFunc: (headerValue, rowValue: SkillSet, rowData, filterParams) => {
                    return Object.values(rowValue)[0].some((value: Skill) => headerValue.indexOf(value.skill) > -1) || headerValue.length == 0
                },
                formatter: this.SkillsFormatter,
                tooltip: function (e, cell) {
                    return cell.getElement().textContent || "";
                },
                vertAlign: "middle",
            },
            {
                title: "Status",
                field: "status",
                width: 75,
                headerFilter: "list",
                headerFilterFunc: (headerValue, rowValue) => {
                    if (!Array.isArray(headerValue)) headerValue = [headerValue];
                    if (headerValue.length === 0) return true;
                    return headerValue.some((val: string) => val === rowValue);
                },
                headerFilterParams: {
                    sort: "asc",
                    values: AssignmentDemand.AllDemandStatuses,
                    multiselect: true
                },
                tooltip: true
            },
            {
                title: "Suggestions",
                field: "suggestions",
                headerFilter: "list",
                tooltip: true,
                sorter: function (a, b) {
                    return a.toString().localeCompare(b.toString());
                },
                headerFilterFunc: (headerValue, rowValue) => {
                    if (!Array.isArray(rowValue)) rowValue = [rowValue];
                    if (rowValue.length === 0) return false;
                    return rowValue.some((val: string[]) => val.includes(headerValue));
                },
                headerFilterParams: {
                    sort: "asc",
                    values: [...new Set(this.assignments.map(assignment => {
                        return assignment.suggestions;
                    }).reduce((previousValue, currentValue) => {
                        return previousValue.concat(currentValue);
                    }, []))],
                    autocomplete: true
                },
                formatter: function (cell, formatterParams, onRendered) {
                    const suggestions = cell.getValue();
                    if (Array.isArray(suggestions)) {
                        return suggestions.map(suggestion => suggestion).join(", ");
                    } else if (typeof suggestions === 'object') {
                        return suggestions.name;
                    }
                    return suggestions.toString();
                }
            },
            {
                title: "Assigned",
                field: "employeeId",
                headerFilter: "list",
                headerFilterFunc: (headerValue, rowValue) => {
                    return checkHeaderFilterFunctionInput(headerValue, rowValue) && containsString(rowValue, headerValue);
                },
                headerFilterParams: {
                    sort: "asc",
                    values: this.assignments
                        .map(a => a.employeeId)
                        .filter((v, i, a) => v !== null && v !== undefined && a.indexOf(v) === i),
                    autocomplete: true
                },
                formatter: this.namesWithIconFormatter,
                tooltip: true
            },
        ];
        this.columnToggleFromLocalStorage(columns);
        const table = new TabulatorFull("#assignment-table", {
            height: 200,
            layout: "fitColumns",
            data: this.assignments,
            columns: columns,
            initialSort: [
                { column: "dueDate", dir: "asc" },
            ],
        });

        table.on("rowClick", this.onRowClick.bind(this));

        table.on("tableBuilt", async () => {
            this.applyFilteredView();
        });

        this.calculateRowsForEachFilter();
        this.table = table;
    }

    createMultiselectHeaderFilter(skillCategoryAndName: Skill[]) {
        return (cell: CellComponent, onRendered: (arg0: () => void) => void, success: (arg0: string | string[] | undefined) => void) => {
            var input = document.createElement("select");
            input.style.width = "100%";
            input.style.height = "auto";
            input.setAttribute('multiple', '');

            skillCategoryAndName.forEach(skill => {
                let skillNamebuilder = skill.category + ": " + skill.skill;
                input.add(new Option(skillNamebuilder, skill.skill));
            });

            onRendered(() => {
                $(input).select2({ closeOnSelect: false })
                    .on('change', () => {
                        success($(input).val());
                        this.table.redraw(true);
                    })
                    .on('select2:select', () => {
                        $('.select2-search__field').val('').trigger('input');
                        $('.select2-search__field').focus();
                    })
                    .on('select2:unselect', () => {
                        $('.select2-search__field').val('').trigger('input');
                        $('.select2-search__field').focus();
                    })
            });
            
            return input;
        };
    }

    SkillsFormatter(cell: CellComponent) {
        let table = cell.getTable();
        let skills = cell.getValue() as SkillSet;
        let filterValue = table.getColumn("skills").getHeaderFilterValue();
        let filteredCategories: string[] = filterValue ? [...new Set((filterValue as string[]).map((filter: string) => filter.split(" - ")[0]))] : [];
        let str = "";

        let skillsMap: { [key: string]: Skill[] } = {};
        for (let skill of skills.skills) {
            if (!skillsMap[skill.category]) {
                skillsMap[skill.category] = [];
            }
            skillsMap[skill.category] = skillsMap[skill.category].concat(skill);
        }

        for (let category of filteredCategories) {
            if (skillsMap[category]) {
                str += `${category}: ${skillsMap[category].join(", ")} | `;
            }
        }

        for (let category in skillsMap) {
            if (!filteredCategories.includes(category) || !filterValue) {
                str += `${category}: ${skillsMap[category].join(", ")} | `;
            }
        }

        // Remove the last " | " from the string
        if (str.endsWith(" | "))
            str = str.slice(0, -3);

        return str;
    }


    /**
     * Create circles with color based on value and fist letters of names
     * @param {Tabulator.CellComponent} cell
     * @param {object} _formatterParams
     * @returns {string}
    */
    namesWithIconFormatter(cell: CellComponent) {
        var bvid = cell.getValue();
        let row = cell.getRow();
        let data = row.getData();

        if (!bvid) return '';

        const icon = createNameIconElementWith2Initials(data.employeeId, "name-cell-icon name-cell-icon--sm mx-2", ".");
        return icon + `<span>${bvid}</span>`;
    }

    subscribedAssignmentChangeHandler = async (assignment: AssignmentDemand, type: string) => {
        let rows = this.table.getRows();
        let row = rows.find(row => {
            let data = row.getData();
            return data.id === assignment.id;
        });
        if (!row || type === "added") {
            this.assignments.push(assignment);
            this.table.replaceData(this.assignments);
            return;
        }
        if (!row) return;

        if (type === "modified") {
            this.assignments = this.assignments.filter(a => a.id !== assignment.id);
            this.assignments.push(assignment);
            row.update(assignment);
            console.log("assignment modified by other user");
        }
        else if (type === "removed") {
            this.assignments = this.assignments.filter(a => a.id !== assignment.id);
            await row.delete();
            console.log("assignment removed by other user");
        }

        await this.calculateRowsForEachFilter();
        row.reformat();
        this.table.refreshFilter();
    }
}